<template>
<v-card class="elevation-4">
  <v-card-actions>
    <span class="title ml-3">{{ title }}</span>
    <v-spacer></v-spacer>
    <v-tooltip bottom v-if="item.width<12" lazy class="hidden-sm-and-down">
      <v-btn icon slot="activator" @click="inflate">
        <v-icon color="grey">fullscreen</v-icon>
      </v-btn>
      <span>{{ $t('kanban.inflate') }}</span>
    </v-tooltip>
    <v-tooltip bottom v-if="!item.width||item.width>4" lazy class="hidden-sm-and-down">
      <v-btn icon slot="activator" @click="deflate">
        <v-icon color="grey">fullscreen_exit</v-icon>
      </v-btn>
      <span>{{ $t('kanban.deflate') }}</span>
    </v-tooltip>
    <v-tooltip bottom>
      <v-btn icon slot="activator" @click="refresh" lazy>
        <v-icon color="grey">refresh</v-icon>
      </v-btn>
      <span>{{ $t('kanban.refresh') }}</span>
    </v-tooltip>
    <v-menu class="right-top-menu-btn">
      <v-btn icon slot="activator">
        <v-icon color="grey">more_vert</v-icon>
      </v-btn>
      <v-list>
        <v-list-tile @click="remove">
          <v-list-tile-action>
            <v-icon color="red">delete</v-icon>
          </v-list-tile-action>
          <v-list-tile-title>{{ $t('kanban.delete') }}</v-list-tile-title>
        </v-list-tile>
        <v-list-tile @click="edit">
          <v-list-tile-action>
            <v-icon color="primary" slot="activator">edit</v-icon>
          </v-list-tile-action>
          <v-list-tile-title>{{ $t('kanban.edit') }}</v-list-tile-title>
        </v-list-tile>
      </v-list>
    </v-menu>
  </v-card-actions>
  <div class="px-2 pb-2">
    <chart-line v-if="isLineChart" :data="data" :options="options" :width="width" :height="height"></chart-line>
    <chart-bar v-if="isBarChart" :data="data" :options="options" :width="width" :height="height"></chart-bar>
  </div>
</v-card>
</template>

<script>
import UserAuth from '@/components/auth/UserAuth'
import colors from 'vuetify/es5/util/colors'
import ChartLine from '@/components/charts/Line'
import ChartBar from '@/components/charts/Bar'
import DeviceUnits from '@/components/device/DeviceUnits'

const colorNames = ['blue', 'red', 'green', 'brown', 'pink', 'cyan', 'orange'];

function getColor(index) {
  let i1 = index % colorNames.length;
  let i2 = Math.floor(index / colorNames.length);
  let name = colorNames[i1];
  let level = 'base';
  if (i2 == 1) level = 'darken3';
  if (i2 == 2) level = 'lighten2';
  return colors[name][level];
}

export default {
  name: 'KanbanItem',
  mixins: [UserAuth],
  components: {
    'chart-line': ChartLine,
    'chart-bar': ChartBar
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    eventlog: {
      type: Object,
      required: true
    }
  },
  computed: {
    title() {
      return this.item.name || this.$t('kanban.' + this.item.type);
    },
    data() {
      return this.dataNormal;
    },
    dataNormal() {
      return {
        datasets: this.item.devices.map((d, i) => {
          let gain = this.getDeviceValueGain(d);
          let offset = this.getDeviceValueOffset(d);
          let color = getColor(i);
          let title = this.getDeviceTitle(d);
          let data = JSON.parse(((this.item.data || {})[d] || {}).result || '[]');
          if (this.isBasicValueUsed) {
            for (let d of data) {
              if (d[2] > 100) d[2] = 100;
            }
          }
          return {
            data: data.map(k => ({
              t: k[0],
              y: k[this.isBasicValueUsed ? 2 : 1] / gain - offset
            })),
            borderWidth: 1.5,
            steppedLine: this.isLineStepped,
            lineTension: 0,
            pointRadius: 0,
            borderColor: color,
            backgroundColor: color,
            label: title,
            fill: false
          };
        })
      };
    },
    options() {
      return {
        legend: {
          display: true
        },
        scales: {
          xAxes: [{
            type: 'time',
            time: {
              unit: this.xAxisUnit,
              min: this.yAxisMinimum,
              max: this.yAxisMaximum
            },
            stacked: this.isBarStacked
          }],
          yAxes: [{
            ticks: {
              beginAtZero: this.unitMinimum == 0,
              suggestedMin: this.unitMinimum,
              suggestedMax: this.unitMaximum,
              callback: function(value, index, values) {
                if (value < 10000)
                  return value;
                else
                  return (value / 1000).toFixed(0) + 'k'
              }
            },
            scaleLabel: {
              display: !!this.unitLabel,
              labelString: this.unitLabel
            }
          }]
        },
        tooltips: {
          intersect: false,
          mode: 'nearest',
          callbacks: {
            beforeBody(items) {
              (items || []).forEach(item => {
                item.value = Math.round(item.value * 100) / 100;
              });
            }
          }
        },
        responsive: true,
        maintainAspectRatio: false,
        animation: false
      };
    },
    width() {
      return 1000;
    },
    height() {
      return 300;
    },
    xAxisUnit() {
      if (this.item.days && this.item.days < 2) return 'hour';
      switch (this.item.eventCode) {
        case 4804:
          if (this.item.chart === 'trend') return null;
      }
      return 'day';
    },
    yAxisMinimum() {
      if (this.item.days)
        return new Date().getTime() - this.item.days * 86400000;
    },
    yAxisMaximum() {
      return new Date().getTime();
    },
    unitPrefer() {
      let unit = this.item.unit;
      if (!unit) {
        let firstDevice = this.item.devices.find(d => d);
        if (firstDevice) {
          let d = this.getDeviceByKey(firstDevice);
          if (d) unit = d.unit;
        }
      }
      return unit;
    },
    unitLabel() {
      let unit = this.unitPrefer;
      if (unit) return DeviceUnits.names[unit];
      switch (this.item.eventCode) {
        case 4101:
          return this.$t('kanban.triggers');
      }
    },
    unitMinimum() {
      switch (this.unitPrefer) {
        case 2:
          return 32;
      }
      return 0;
    },
    unitMaximum() {
      switch (this.unitPrefer) {
        case 1:
          return 45;
        case 2:
          return 120;
        case 3:
        case 8:
          return 100;
      }
    },
    isBasicValueUsed() {
      switch (this.item.chart) {
        case 'stepped':
          return true;
      }
      return false;
    },
    isLineChart() {
      switch (this.item.chart) {
        case 'trend':
        case 'consumption':
        case 'stepped':
          return true;
      }
      return false;
    },
    isLineStepped() {
      switch (this.item.chart) {
        case 'stepped':
          return true;
      }
      return false;
    },
    isBarChart() {
      switch (this.item.chart) {
        case 'histogram':
          return true;
      }
      return false;
    },
    isBarStacked() {
      return this.isBarChart;
    }
  },
  methods: {
    accumulatedArray(arr) {
      arr.reduce((prev, curr, i) => {
        if (prev) {
          let acc = curr.map((c, j) => curr[j] + prev[j]);
          arr[i] = acc;
          return acc;
        }
        return curr;
      }, false);
    },
    getDeviceByKey(key) {
      let [mac, uid, cid] = key.split('-');
      let gateway = this.userDevice[mac] || {};
      return ((gateway[`${mac}-${uid}`] || {}).channel || {})[`${mac}-${uid}-${cid}`];
    },
    getDeviceTitle(key) {
      let [mac, uid, cid] = key.split('-');
      let hn = (this.userMacInfo[mac] || {}).hostname;
      let tn = ((this.userTargetName[mac] || {})[uid] || {})[cid];
      if (tn) return [hn, tn].join(' ');
      let d = this.getDeviceByKey(key);
      if (d && d.name) return d.name;
      return key;
    },
    getDeviceValueGain(key) {
      let d = this.getDeviceByKey(key);
      if (d) {
        let gain = 1;
        let unitIndex = this.item.unit || d.unit;
        switch (this.item.chart) {
          case 'stepped':
            unitIndex = 3;
            break;
        }
        if (unitIndex) gain = DeviceUnits.factors[unitIndex];
        if (gain) return gain * this.getTemperatureConvertGain(d.unit, this.item.unit);
      }
      return 1;
    },
    getDeviceValueOffset(key) {
      let d = this.getDeviceByKey(key);
      if (d) {
        return this.getTemperatureConvertOffset(d.unit, this.item.unit);
      }
      return 0;
    },
    getTemperatureConvertGain(unitFrom, unitTo) {
      if (unitFrom === 1 && unitTo === 2) return 5 / 9;
      if (unitFrom === 2 && unitTo === 1) return 9 / 5;
      return 1;
    },
    getTemperatureConvertOffset(unitFrom, unitTo) {
      if (unitFrom === 1 && unitTo === 2) return -32;
      if (unitFrom === 2 && unitTo === 1) return 32 * 5 / 9;
      return 0;
    },
    refresh() {
      this.$emit('action', 'refresh', this.item.key);
    },
    remove() {
      this.$emit('action', 'remove', this.item.key);
    },
    edit() {
      this.$emit('action', 'edit', this.item.key);
    },
    inflate() {
      this.$emit('action', 'inflate', this.item.key);
    },
    deflate() {
      this.$emit('action', 'deflate', this.item.key);
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/juice/KanbanItem.vue